import { useState, type FormEvent } from "react";
import type {
  AdminActivityEntry,
  Locale,
  StaffMember
} from "../../shared/dto";
import { isTestTenant } from "../../shared/test-tenant";
import { StripeImportPanel } from "../StripeImportPanel";
import { AdminBotTransfer } from "./AdminBotTransfer";
import { AdminOwnerRole } from "./AdminOwnerRole";
import { AdminCalls } from "./AdminCalls";
// The team detail labels a member's activity with the SAME presenter the
// timeline uses, so one event never reads two ways. A re-export alone would
// not bind the name here, only republish it.
import { AdminActivityLog, adminActivityPresentation } from "./AdminActivity";
import { AdminSettingsContent } from "./AdminSettingsContent";
import { AdminTranscriptRescue } from "./AdminSettingsDiagnostics";
import { AdminDossierImport } from "./AdminSettingsDossier";
import { AdminImports } from "./AdminImports";
import { AdminClientCreatedCard, useClientCreation } from "./AdminImportsCreate";
import { AdminImportsFeedback } from "./AdminImportsShared";
import { AdminNotifications } from "./AdminNotifications";
import { AdminOps } from "./AdminOps";
import { AdminGroup, AdminPanel } from "./AdminPanel";
import "./admin-console.css";
import { AdminTeam, useRoster, type TeamMemberActivity } from "./AdminTeam";
import {
  type StaffMatrixClient,
  type StaffRun
} from "./model";

// Servis wave 5 moved the activity timeline, the default content and the
// recovery tools into their own modules (one file per feedback area). The
// presentation helpers keep their public path here so existing call sites and
// audit guards do not have to chase the split.
export {
  visibleAdminActivityEntries,
  presentAdminActivity,
  adminActivityStep,
  adminActivityPresentation
} from "./AdminActivity";

interface AdminToolsProps {
  t: (key: string) => string;
  locale: Locale;
  run: StaffRun;
  refresh: () => Promise<void>;
  csrfToken: string;
  clients: StaffMatrixClient[];
  hideTestTenants: boolean;
  onHideTestTenantsChange: (hide: boolean) => void;
  currentUserId?: string;
  /** Operations home rows lead to the client they are about. */
  onOpenClient?: (clientId: string) => void;
  /**
   * F138d: `?staff=admin&tool=<id>` opens exactly one panel - the one the link
   * is about. Everything else stays shut, which is the whole point of the
   * rule: the surface opens as a list of thirteen names, not as a wall.
   */
  openTool?: string | null;
}

export function AdminTools(props: AdminToolsProps) {
  const roster = useRoster();
  const opens = (tool: string) => props.openTool === tool;
  const visibleMembers = props.hideTestTenants
    ? roster.members.filter((member) => !isTestTenant(member.displayName ?? "", member.email))
    : roster.members;
  const activeMembers = visibleMembers.filter((member) => member.isActive);
  // How many ACTIVE members the test filter hid: the member selects use it to
  // explain an emptied list instead of going silent (audit a3-05).
  const hiddenActiveMembers = roster.members.filter((member) => member.isActive).length - activeMembers.length;
  return (
    <section className="admin-tools admin-console" aria-labelledby="admin-tools-title">
      {/* F138d: the surface wears the same head every other console surface
          wears (Dnes, Klienti, Detail): one h1 and the controls that belong to
          the WHOLE page. The test filter is one of those - it reaches into
          every list below it, which is exactly why it used to sit in the
          drawer's own header and why it belongs in this one now. */}
      <div className="staff-section-heading">
        <div>
          <h1 id="admin-tools-title">{props.t("staffConsole.adminZone.title")}</h1>
          <p className="admin-console-lead">{props.t("staffConsole.adminZone.lead")}</p>
        </div>
        <label className="checkbox-row admin-test-filter">
          <input type="checkbox" checked={props.hideTestTenants}
            onChange={(event) => props.onHideTestTenantsChange(event.target.checked)} />
          <span>{props.t("staffConsole.hideTestTenants")}</span>
        </label>
      </div>

      {/* Four groups, four questions. The order is the order an administrator
          asks them in: is it running, how do clients get here, what runs by
          itself, who did what. */}
      <AdminGroup group="operations"
        title={props.t("staffConsole.adminGroup.operations.title")}
        lead={props.t("staffConsole.adminGroup.operations.lead")}>
        <AdminPanel tool="ops" defaultOpen={opens("ops")}
          title={props.t("staffConsole.adminOps.title")}
          lead={props.t("staffConsole.adminOps.hint")}>
          <AdminOps t={props.t} locale={props.locale} csrfToken={props.csrfToken}
            hideTestTenants={props.hideTestTenants}
            onHideTestTenantsChange={props.onHideTestTenantsChange}
            onOpenClient={props.onOpenClient} />
        </AdminPanel>
        {/* Servis wave 4: one notification queue. S3-12: the question this
            panel exists for is about ONE client, so it carries the same client
            picker the timeline has. */}
        <AdminPanel tool="notifications" defaultOpen={opens("notifications")}
          title={props.t("staffConsole.adminNotifications.title")}
          lead={props.t("staffConsole.adminNotifications.hint")}>
          <AdminNotifications t={props.t} locale={props.locale} csrfToken={props.csrfToken}
            hideTestTenants={props.hideTestTenants}
            onHideTestTenantsChange={props.onHideTestTenantsChange}
            onOpenClient={props.onOpenClient}
            clients={props.clients.map(({ id, name, email }) => ({ id, name, email }))} />
        </AdminPanel>
        {/* "Opakovat přepis" was an action; the panel is a LIST of stuck ones,
            and its inner heading always said so. A recovery operation is not
            everyday work, so it sits last inside its own group (master plan
            section 5) instead of last on the whole surface, where an admin
            looking for it had to scroll past everything else. */}
        <AdminPanel tool="transcript-retry" defaultOpen={opens("transcript-retry")}
          title={props.t("staffConsole.diagnostics.transcripts.title")}
          lead={props.t("staffConsole.diagnostics.transcripts.hint")}>
          <AdminTranscriptRescue csrfToken={props.csrfToken} locale={props.locale}
            hideTestTenants={props.hideTestTenants}
            onHideTestTenantsChange={props.onHideTestTenantsChange}
            onOpenClient={props.onOpenClient} t={props.t} />
        </AdminPanel>
      </AdminGroup>

      {/* Wave 3 keeps the intake together: the queue reads the result of both
          import tools, and its fix hints send the admin straight into the
          Stripe mappings, so they must not sit pages apart. */}
      <AdminGroup group="clients"
        title={props.t("staffConsole.adminGroup.clients.title")}
        lead={props.t("staffConsole.adminGroup.clients.lead")}>
        <AdminPanel tool="create-client" defaultOpen={opens("create-client")}
          title={props.t("staffConsole.createClient")}
          lead={props.t("staffConsole.adminPanel.createClient.lead")}>
          <CreateClientForm {...props} partners={activeMembers} hiddenPartnerCount={hiddenActiveMembers} />
        </AdminPanel>
        <AdminPanel tool="imports" defaultOpen={opens("imports")}
          title={props.t("staffConsole.adminImports.title")}
          lead={props.t("staffConsole.adminImports.hint")}>
          <AdminImports t={props.t} locale={props.locale} csrfToken={props.csrfToken}
            hideTestTenants={props.hideTestTenants}
            onHideTestTenantsChange={props.onHideTestTenantsChange}
            onOpenClient={props.onOpenClient} />
        </AdminPanel>
        <AdminPanel tool="stripe-import" defaultOpen={opens("stripe-import")}
          title={props.t("staffConsole.stripeImport.title")}
          lead={props.t("staffConsole.adminPanel.stripe.lead")}>
          <StripeImportPanel {...props} />
        </AdminPanel>
        {/* One name: the summary used to say "Importovat dossier" and the body
            "Import dossieru". The body's copy won, because it names the thing
            the panel contains. */}
        <AdminPanel tool="dossier-import" defaultOpen={opens("dossier-import")}
          title={props.t("staffConsole.dossierWizard.title")}
          lead={props.t("staffConsole.dossierWizard.hint")}>
          <AdminDossierImport clients={props.clients} csrfToken={props.csrfToken} locale={props.locale} t={props.t} />
        </AdminPanel>
      </AdminGroup>

      {/* The audit's "Skupiny si odporují": the bot transfer used to be filed
          under "Klienti a importy" and the owner role under "Automatizace",
          while they are the two halves of ONE handover, armed and rehearsed
          together. They are neighbours here, in the group that answers "what
          runs by itself". */}
      <AdminGroup group="automations"
        title={props.t("staffConsole.adminGroup.automations.title")}
        lead={props.t("staffConsole.adminGroup.automations.lead")}>
        <AdminPanel tool="bot-transfer" defaultOpen={opens("bot-transfer")}
          title={props.t("staffConsole.botTransfer.title")}
          lead={props.t("staffConsole.adminPanel.botTransfer.lead")}>
          <AdminBotTransfer t={props.t} csrfToken={props.csrfToken}
            hideTestTenants={props.hideTestTenants}
            onHideTestTenantsChange={props.onHideTestTenantsChange}
            onOpenClient={props.onOpenClient} />
        </AdminPanel>
        <AdminPanel tool="owner-role" defaultOpen={opens("owner-role")}
          title={props.t("staffConsole.ownerRole.title")}
          lead={props.t("staffConsole.adminPanel.ownerRole.lead")}>
          <AdminOwnerRole t={props.t} csrfToken={props.csrfToken}
            hideTestTenants={props.hideTestTenants}
            onHideTestTenantsChange={props.onHideTestTenantsChange}
            onOpenClient={props.onOpenClient} />
        </AdminPanel>
        <AdminPanel tool="video-defaults" defaultOpen={opens("video-defaults")}
          title={props.t("staffConsole.videos.defaultsTitle")}
          lead={props.t("staffConsole.settingsContent.hint")}>
          <AdminSettingsContent csrfToken={props.csrfToken} locale={props.locale}
            hideTestTenants={props.hideTestTenants} t={props.t} />
        </AdminPanel>
        <AdminPanel tool="calls" defaultOpen={opens("calls")}
          title={props.t("staffConsole.adminCalls.title")}
          lead={props.t("staffConsole.adminCalls.hint")}>
          <AdminCalls t={props.t} locale={props.locale} csrfToken={props.csrfToken}
            clients={props.clients.map(({ id, name, email }) => ({ id, name, email }))} />
        </AdminPanel>
      </AdminGroup>

      <AdminGroup group="people"
        title={props.t("staffConsole.adminGroup.people.title")}
        lead={props.t("staffConsole.adminGroup.people.lead")}>
        {/* Servis wave 4: the roster, the member profile, their clients and
            their access facts are ONE detail (src/staff/AdminTeam.tsx).
            S3-02: the WHOLE team goes in. The panel filters it itself, so the
            two halves of "Zobrazeno záznamů ... Skryto testovacích" are
            measured on one population. */}
        <AdminPanel tool="team" defaultOpen={opens("team")}
          title={props.t("staffConsole.team.title")}
          lead={props.t("staffConsole.adminTeam.hint")}>
          <AdminTeam t={props.t} locale={props.locale} csrfToken={props.csrfToken}
            roster={roster} refresh={props.refresh}
            hideTestTenants={props.hideTestTenants}
            onHideTestTenantsChange={props.onHideTestTenantsChange}
            onOpenClient={props.onOpenClient}
            activityLabel={(entry: TeamMemberActivity) =>
              adminActivityPresentation(entry as AdminActivityEntry, props.t).title} />
        </AdminPanel>
        {/* "Nedávná aktivita" outside, "Časová osa" inside: an admin looking
            for the timeline could not find it in the list. The content's name
            wins here too, and it is the one the docs already use. */}
        <AdminPanel tool="activity" defaultOpen={opens("activity")}
          title={props.t("staffConsole.activityLog.title")}
          lead={props.t("staffConsole.activityLog.hint")}>
          <AdminActivityLog t={props.t} locale={props.locale} hideTestTenants={props.hideTestTenants}
            onHideTestTenantsChange={props.onHideTestTenantsChange}
            onOpenClient={props.onOpenClient}
            clients={props.clients.map(({ id, name, email }) => ({ id, name, email }))} />
        </AdminPanel>
      </AdminGroup>
    </section>
  );
}

/**
 * One member select for every admin form. The global test filter used to
 * empty these selects down to a bare "Vyber" with no explanation, turning
 * "Založit klienta" into a dead end (audit a3-05). An emptied select now says
 * WHY it is empty and offers the way out; options carry the e-mail so two
 * members with the same display name stay tellable apart.
 */
function MemberSelect(props: {
  /** D14: the select and its label are paired explicitly, so a screen reader
   *  reads "Parťák" on the control instead of an unnamed combobox. */
  selectId: string;
  label: string;
  members: StaffMember[];
  hiddenCount: number;
  value: string;
  onChange: (id: string) => void;
  t: (key: string) => string;
  onShowTestMembers?: () => void;
  /** F139: a partner is optional on the client forms ("Priradim pozdeji"), so
   *  the empty option NAMES that choice instead of being an unfilled field. */
  emptyOptionKey?: string;
  optional?: boolean;
}) {
  if (props.members.length === 0 && props.hiddenCount > 0 && !props.optional) {
    return (
      <div className="inline-select member-select-empty">
        <span>{props.label}</span>
        <p className="admin-tool-hint">{props.t("staffConsole.team.allFiltered")}</p>
        {props.onShowTestMembers && (
          <button className="secondary-button compact-button" type="button" onClick={props.onShowTestMembers}>
            {props.t("staffConsole.team.showTestMembers")}
          </button>
        )}
      </div>
    );
  }
  return (
    <label className="inline-select" htmlFor={props.selectId}>
      <span>{props.label}</span>
      <select
        id={props.selectId}
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
        required={!props.optional}
      >
        <option value="">{props.t(props.emptyOptionKey ?? "staffConsole.team.choose")}</option>
        {props.members.map((member) => (
          <option key={member.id} value={member.id}>
            {`${member.displayName ?? member.email} (${props.t(`staffConsole.role.${member.role}`)}) · ${member.email}`}
          </option>
        ))}
      </select>
    </label>
  );
}

const EMPTY_CLIENT_VALUES = {
  name: "", ownerEmail: "", ownerDisplayName: "", partnerId: "",
  purchaseSource: "direct", instanceStartMode: "new",
  // F143: `full` is what almost every client is and what the column defaults
  // to, so it is preselected rather than left unchosen. Unlike the market
  // language (F116), a wrong answer here is visible on the client detail the
  // moment it is created and correctable in the editor next door.
  journeyMode: "full", includesApp: false
};

function CreateClientForm(props: AdminToolsProps & { partners: StaffMember[]; hiddenPartnerCount: number }) {
  // F116: the language starts UNCHOSEN. It used to be pre-filled with the
  // ADMIN's own interface language, so every client a Czech admin created by
  // hand became a Czech client without anybody stating it - and from that one
  // silent value the whole dashboard, the e-mails, the questionnaire and the
  // generated materials follow. The Stripe panel's twin does the same.
  const [values, setValues] = useState({ ...EMPTY_CLIENT_VALUES, marketLanguage: "" as Locale | "" });
  // Wave 3: the result of a creation is a card naming the client, its owner,
  // its partner and every automation that really started, not a generic toast.
  const creation = useClientCreation({ run: props.run, refresh: props.refresh });
  async function submit(event: FormEvent) {
    event.preventDefault();
    const marketLanguage = values.marketLanguage;
    if (!marketLanguage) return;
    // A failed attempt keeps the filled form: the safe repeat is one click.
    if (await creation.create({ ...values, marketLanguage })) {
      setValues({ ...EMPTY_CLIENT_VALUES, marketLanguage: "" });
    }
  }
  if (creation.state === "done") {
    return <AdminClientCreatedCard summary={creation.summary} fallbackClientId={creation.fallbackClientId}
      t={props.t} onReset={creation.reset} />;
  }
  return (
    <form className="admin-tool-form" onSubmit={submit}>
      <label htmlFor="create-client-name"><span>{props.t("staffConsole.clientName")}</span></label>
      <input id="create-client-name" placeholder={props.t("staffConsole.clientNameExample")} value={values.name} onChange={(event) => setValues({ ...values, name: event.target.value })} required />
      <label htmlFor="create-client-owner-email"><span>{props.t("staffConsole.ownerEmail")}</span></label>
      <input id="create-client-owner-email" type="email" placeholder={props.t("staffConsole.ownerEmailExample")} value={values.ownerEmail} onChange={(event) => setValues({ ...values, ownerEmail: event.target.value })} required />
      <label htmlFor="create-client-owner-name"><span>{props.t("staffConsole.ownerName")}</span></label>
      <input id="create-client-owner-name" placeholder={props.t("staffConsole.ownerNameExample")} value={values.ownerDisplayName} onChange={(event) => setValues({ ...values, ownerDisplayName: event.target.value })} required />
      <MemberSelect
        selectId="create-client-partner"
        label={props.t("staffConsole.partner")}
        members={props.partners}
        hiddenCount={props.hiddenPartnerCount}
        value={values.partnerId}
        onChange={(partnerId) => setValues({ ...values, partnerId })}
        t={props.t}
        onShowTestMembers={() => props.onHideTestTenantsChange(false)}
        emptyOptionKey="staffConsole.team.assignLater"
        optional
      />
      {/* S3-08: one word for one field. This select and its twin in the Stripe
          panel write the SAME column, and they were labelled "Jazyk hovoru"
          and nothing at all. Since F81 the two steps this used to name are
          videos, so the honest word is the client's language. */}
      <label className="inline-select" htmlFor="create-client-locale">
        <span>{props.t("staffConsole.clientLocale")}</span>
        <select
          id="create-client-locale"
          value={values.marketLanguage}
          onChange={(event) => setValues({ ...values, marketLanguage: event.target.value as Locale })}
          required
        >
          <option value="">{props.t("staffConsole.stripeImport.selectLanguage")}</option>
          <option value="cs">{props.t("common.languageCs")}</option>
          <option value="en">{props.t("common.languageEn")}</option>
        </select>
      </label>
      <label className="inline-select" htmlFor="create-client-purchase">
        <span>{props.t("staffConsole.purchaseMode")}</span>
        <select id="create-client-purchase" value={values.purchaseSource} onChange={(event) => setValues({ ...values, purchaseSource: event.target.value })}>
          <option value="direct">{props.t("staffConsole.axis.purchase_direct")}</option>
          <option value="upsell">{props.t("staffConsole.axis.purchase_upsell")}</option>
          <option value="webinar">{props.t("staffConsole.axis.purchase_webinar")}</option>
        </select>
      </label>
      <label className="inline-select" htmlFor="create-client-instance">
        <span>{props.t("staffConsole.instanceMode")}</span>
        <select id="create-client-instance" value={values.instanceStartMode} onChange={(event) => setValues({ ...values, instanceStartMode: event.target.value })}>
          <option value="new">{props.t("staffConsole.axis.instance_new")}</option>
          <option value="existing">{props.t("staffConsole.axis.instance_existing")}</option>
        </select>
      </label>
      {/* D14: the select carries an id and its label points at it. */}
      <label className="inline-select" htmlFor="create-client-journey">
        <span>{props.t("staffConsole.journeyMode")}</span>
        <select id="create-client-journey" value={values.journeyMode} onChange={(event) => setValues({ ...values, journeyMode: event.target.value })}>
          <option value="full">{props.t("staffConsole.journey.full")}</option>
          <option value="sales_only">{props.t("staffConsole.journey.sales_only")}</option>
        </select>
      </label>
      <p className="admin-tool-hint">{props.t("staffConsole.journeyHint")}</p>
      <label className="checkbox-row">
        <input type="checkbox" checked={values.includesApp} onChange={(event) => setValues({ ...values, includesApp: event.target.checked })} />
        <span>{props.t("staffConsole.includesApp")}</span>
      </label>
      <button className="primary-button" type="submit" disabled={creation.state === "working" || !values.marketLanguage}>
        {props.t("staffConsole.create")}
      </button>
      {/* S2-19: a dead control names its condition beside itself. */}
      {!values.marketLanguage && (
        <p className="admin-tool-hint">{props.t("staffConsole.stripeImport.addMappingNeedsLanguage")}</p>
      )}
      <AdminImportsFeedback state={creation.state} message={null} t={props.t}
        working={props.t("staffConsole.adminImports.created.working")}
        error={props.t(creation.errorKey ?? "staffConsole.adminImports.created.failed")} />
    </form>
  );
}

interface AdminOperationItem { id: string; clientId: string | null; label: string; isTest?: boolean; }

/**
 * Operation lists honour the global test filter (audit a3-04). Waves 4 and 5
 * replaced the picker selects this was written for with their own row lists,
 * so it has no caller left on the surface; it stays exported because the
 * filtering law it states is still the one the panels follow, and fixr4 guards
 * it. Retiring it belongs to the same cleanup pass as the orphaned copy keys.
 */
export function visibleOperationItems(
  items: AdminOperationItem[],
  hideTestTenants: boolean
): AdminOperationItem[] {
  return hideTestTenants ? items.filter((item) => !item.isTest) : items;
}
